import { log } from "./logger";
import { DialogMessageType, DialogMessage } from "../types/commands";
import { extractSlideImages } from "./formatTemplate/slides-extraction-handlers";

export async function handleMessage(
  args: Office.DialogParentMessageReceivedEventArgs,
  dialog: Office.Dialog,
  selectionHandler: OfficeExtension.EventHandlerResult<Excel.SelectionChangedEventArgs> | undefined
) {
  if (args.message === "EXTRACT_SLIDES") {
    await extractSlideImages();
    return;
  }
  // For other messages, parse as JSON
  const data = JSON.parse(args.message) as DialogMessage;
  switch (data.action) {
    case DialogMessageType.OPEN_PLACEHOLDER:
      openPlaceholder(dialog, selectionHandler);
      break;
    case DialogMessageType.START_CELL_SELECTION:
      setupCellSelection(dialog, selectionHandler);
      break;
    case DialogMessageType.END_CELL_SELECTION:
      endCellSelection(data.data, dialog, selectionHandler);
      break;
    case DialogMessageType.ADD_LINK_TO_CELL:
      addLinkToCell(data.data, dialog);
      break;
    case DialogMessageType.ADD_CUSTOM_BUILT_INDEX_FORMULA_TO_CELL:
      addCustomBuiltIndexFormulaToCell(data.data, dialog);
      break;
    case DialogMessageType.CLOSE:
      await dialog.close();
      break;
  }
} 

function setupCellSelection(
  dialog: Office.Dialog,
  selectionHandler: OfficeExtension.EventHandlerResult<Excel.SelectionChangedEventArgs> | undefined
) {
  Excel.run(async (context) => {
    const workbook = context.workbook;
    if (selectionHandler) selectionHandler.remove();

    selectionHandler = workbook.onSelectionChanged.add(async () => {
      try {
        await Excel.run(async (context) => {
          const range = context.workbook.getSelectedRange();
          const activeWorksheet = context.workbook.worksheets.getActiveWorksheet();
          range.load("address");
          activeWorksheet.load("name");
          await context.sync();

          if (dialog) {
            // Extract sheet name and cell address from range.address
            // Format can be: "Sheet1!A1" or "'Sheet Name'!A1:B2"
            const fullAddress = range.address;
            const activeSheetName = activeWorksheet.name;
            let sheetName: string | null = null;
            let cellAddress: string = "";
            
            if (fullAddress.includes("!")) {
              const addressParts = fullAddress.split("!");
              // Remove quotes if present (e.g., "'Sheet Name'" -> "Sheet Name")
              sheetName = addressParts[0].replace(/^'|'$/g, "");
              cellAddress = addressParts[1] || addressParts[0];
            } else {
              // If no sheet name in address, use activeWorksheet.name
              cellAddress = fullAddress;
              sheetName = activeSheetName || null;
            }
            
            // Always use activeWorksheet.name as the source of truth
            const finalSheetName = activeSheetName || sheetName || null;
            
            const messagePayload = {
              action: DialogMessageType.UPDATE_ADDRESS,
              address: cellAddress,
              sheetName: finalSheetName !== undefined && finalSheetName !== null ? finalSheetName : null,
            };
            
            const messageString = JSON.stringify(messagePayload);
            dialog.messageChild(messageString);
          }
        });
      } catch (error) {
        log.error("Error:", error);
      }
    });

    await context.sync();
  }).catch(log.error);
}

function endCellSelection(
  data: { address: string },
  dialog: Office.Dialog,
  selectionHandler: OfficeExtension.EventHandlerResult<Excel.SelectionChangedEventArgs> | undefined
) {
  log.info("Ending cell selection");
  if (selectionHandler) selectionHandler.remove();
  dialog.close();
  dialog = null;

  setTimeout(() => {
    const frontendUrl = process.env.REACT_APP_PUBLIC_URL;
    const popupUrl = `${frontendUrl}popup.html`;
    localStorage.setItem("cellAddress", data.address);

    Office.context.ui.displayDialogAsync(popupUrl, { height: 75, width: 70, displayInIframe: true }, (result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        log.error("Failed to open dialog:", result.error.message);
      } else {
        dialog = result.value;
        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          async (args: Office.DialogParentMessageReceivedEventArgs) =>
            await handleMessage(args, dialog, selectionHandler)
        );
      }
    });
  }, 1000);
}

function addLinkToCell(data: { formula: string; address: string }, dialog: Office.Dialog) {
  dialog.close();
  Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getActiveWorksheet();

    try {
      const range = sheet.getRange(data.address);
      range.load("address");
      await context.sync();

      // Use formulas property and ensure the formula is properly formatted
      range.formulas = [[`${data.formula}`]];
      await context.sync();
    } catch (error) {
      log.error("Error:", error);
    }
  }).catch(log.error);
}

function addCustomBuiltIndexFormulaToCell(data: { formula: string; address: string }, dialog: Office.Dialog) {
  dialog.close();
  Excel.run(async (context) => {
    try {
      // Extract sheet name and cell address from address (handle format like 'SheetName'!A1)
      let sheetName: string | null = null;
      let cellAddress = data.address;
      
      if (data.address.includes('!')) {
        const parts = data.address.split('!');
        // Remove quotes if present (e.g., "'Sheet Name'" -> "Sheet Name")
        sheetName = parts[0].replace(/^'|'$/g, "");
        cellAddress = parts[1] || parts[0];
      }
      
      // Get the worksheet (use specific sheet if name provided, otherwise use active sheet)
      const sheet = sheetName 
        ? context.workbook.worksheets.getItem(sheetName)
        : context.workbook.worksheets.getActiveWorksheet();

      const range = sheet.getRange(cellAddress);
      range.load("address");
      await context.sync();

      // Use formulas property and ensure the formula is properly formatted
      // Remove leading '=' if present (formula already has it)
      const cleanFormula = data.formula.startsWith('=') ? data.formula : `=${data.formula}`;
      range.formulas = [[cleanFormula]];
      await context.sync();
    } catch (error) {
      log.error("Error inserting custom built index formula into Excel:", error);
    }
  }).catch(log.error);
} 

function openPlaceholder(
  dialog: Office.Dialog,
  selectionHandler: OfficeExtension.EventHandlerResult<Excel.SelectionChangedEventArgs> | undefined
) {
  if (dialog) {
    dialog.close();
    dialog = null;
  }
  setTimeout(() => {
    const frontendUrl = process.env.REACT_APP_PUBLIC_URL;
    const placeholderUrl = `${frontendUrl}placeholder.html`;

    Office.context.ui.displayDialogAsync(placeholderUrl, { height: 3, width: 20, displayInIframe: true }, (result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        log.error("Failed to open dialog:", result.error.message);
      } else {
        dialog = result.value;
        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          async (args: Office.DialogParentMessageReceivedEventArgs) =>
            await handleMessage(args, dialog, selectionHandler)
        );
      }
    });
  }, 1000);
}
